import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  DollarSign, Save, RefreshCw, Filter,
} from 'lucide-react';

const JA_INDUSTRIES = ['Healthcare', 'Hospitality', 'Industrial', 'Retail', 'Technology', 'Construction', 'Education', 'Security'];
const JA_CATEGORIES = ['On-site', 'Remote', 'Hybrid'];
const JA_SKILLS = ['Nursing', 'CPR', 'First Aid', 'CNA', 'Medication Administration', 'Patient Care', 'Forklift', 'Cleaning', 'Cooking', 'Customer Service', 'Security', 'Driving', 'Childcare', 'Elder Care', 'Vital Signs'];

const DEFAULT_PREFS = {
  enabled: true,
  categories: [],
  industries: [],
  skills: [],
  min_pay_rate: 0,
  notify_surge: true,
};

/**
 * Job alert preferences form (in Worker Settings → Job Alerts tab).
 * Owns its own state and persistence — pure standalone widget.
 */
export default function JobAlertPreferences() {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall('/api/worker/job-alerts');
        setPrefs(res.preferences || DEFAULT_PREFS);
      } catch {
        // Fall back to defaults
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiCall('/api/worker/job-alerts/preferences', {
        method: 'PUT',
        body: JSON.stringify(prefs),
      });
      toast.success('Job alert preferences saved!');
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const toggle = (field, val) => setPrefs((p) => ({
    ...p,
    [field]: (p[field] || []).includes(val)
      ? p[field].filter((v) => v !== val)
      : [...(p[field] || []), val],
  }));

  const activeFilters = [
    ...(prefs.categories || []),
    ...(prefs.industries || []),
    ...(prefs.skills || []),
    ...(prefs.min_pay_rate > 0 ? [`$${prefs.min_pay_rate}+/hr`] : []),
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="job-alert-preferences-section">
      {activeFilters.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="profile-filter-summary">
          <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
            <Filter className="w-3.5 h-3.5" /> Active Filter Summary
          </p>
          <div className="flex flex-wrap gap-1.5">
            {activeFilters.map((f) => (
              <span key={f} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{f}</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
        <div>
          <p className="font-medium text-gray-900">Job Alerts</p>
          <p className="text-sm text-gray-500">Receive alerts when matching jobs are posted</p>
        </div>
        <button
          type="button"
          onClick={() => setPrefs((p) => ({ ...p, enabled: !p.enabled }))}
          className={`w-12 h-6 rounded-full transition ${prefs.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
          data-testid="alert-enabled-toggle"
        >
          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${prefs.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-800 mb-2">Job Category</p>
        <div className="flex flex-wrap gap-2">
          {JA_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => toggle('categories', c)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${(prefs.categories || []).includes(c) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
              data-testid={`profile-cat-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-800 mb-2">Industries</p>
        <div className="flex flex-wrap gap-2">
          {JA_INDUSTRIES.map((ind) => (
            <button
              key={ind}
              type="button"
              onClick={() => toggle('industries', ind)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${(prefs.industries || []).includes(ind) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
              data-testid={`profile-ind-${ind.toLowerCase()}`}
            >
              {ind}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-800 mb-2">Required Skills</p>
        <div className="flex flex-wrap gap-2">
          {JA_SKILLS.map((sk) => (
            <button
              key={sk}
              type="button"
              onClick={() => toggle('skills', sk)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${(prefs.skills || []).includes(sk) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'}`}
              data-testid={`profile-skill-${sk.toLowerCase().replace(/\s/g, '-')}`}
            >
              {sk}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-800 mb-2">Minimum Pay Rate</p>
        <div className="flex items-center gap-3">
          <DollarSign className="w-5 h-5 text-gray-400" />
          <input
            type="number"
            min="0"
            step="5"
            value={prefs.min_pay_rate || 0}
            onChange={(e) => setPrefs((p) => ({ ...p, min_pay_rate: parseInt(e.target.value, 10) || 0 }))}
            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
            data-testid="profile-min-pay"
          />
          <span className="text-sm text-gray-500">/hr</span>
        </div>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
        <div>
          <p className="font-medium text-gray-900">Surge Alerts</p>
          <p className="text-sm text-gray-500">Get notified about high-demand surge shifts</p>
        </div>
        <button
          type="button"
          onClick={() => setPrefs((p) => ({ ...p, notify_surge: !p.notify_surge }))}
          className={`w-12 h-6 rounded-full transition ${prefs.notify_surge ? 'bg-blue-600' : 'bg-gray-300'}`}
          data-testid="profile-surge-toggle"
        >
          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${prefs.notify_surge ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="save-job-alert-prefs-btn"
      >
        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? 'Saving...' : 'Save Alert Preferences'}
      </button>
    </div>
  );
}
